import React, { useState, Suspense } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  
  Alert,
  CircularProgress,
} from '@mui/material';
import { EventType } from '../../types/firebase';
import { createEvent } from '../../services/eventService';
import { CAUSES } from '../layout/causes';
import { EVENT_TYPES } from '../../constants/eventTypes';
import { useAuth } from '../../contexts/AuthContext';
import { Timestamp } from '@firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const LocalizationProvider = React.lazy(() => import('@mui/x-date-pickers/LocalizationProvider').then(mod => ({ default: mod.LocalizationProvider })));
const DatePicker = React.lazy(() => import('@mui/x-date-pickers/DatePicker').then(mod => ({ default: mod.DatePicker })));
const TimePicker = React.lazy(() => import('@mui/x-date-pickers/TimePicker').then(mod => ({ default: mod.TimePicker })));

interface FormData {
  title: string;
  description: string;
  eventType: EventType;
  cause: string;
  date: Date | null;
  time: Date | null;
  location: {
    address: string;
    city: string;
    state: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  link?: string;
  visibility: 'public' | 'private';
}

interface CreateEventFormProps {
  onSuccess?: () => void;
}

export default function CreateEventForm({ onSuccess }: CreateEventFormProps) {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    eventType: 'other',
    cause: 'climate-change',
    date: null,
    time: null,
    location: {
      address: '',
      city: '',
      state: '',
      country: ''
    },
    link: '',
    visibility: 'public'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    if (!formData.time) {
      newErrors.time = 'Time is required';
    }
    if (!formData.location.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.location.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.location.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.location.country.trim()) {
      newErrors.country = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      setSubmitError('You are not logged in');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitError('');
      
      // Get the current date/time
      const eventDate = formData.date;
      const eventTime = formData.time;
      
      if (!eventDate || !eventTime) {
        setSubmitError('Invalid date or time');
        return;
      }

      // Combine date and time
      const combinedDateTime = new Date(
        eventDate.getFullYear(),
        eventDate.getMonth(),
        eventDate.getDate(),
        eventTime.getHours(),
        eventTime.getMinutes()
      );

      // Check if date is in the past
      if (combinedDateTime < new Date()) {
        setSubmitError('Date and time must be in the future');
        return;
      }

      const location = {
        address: formData.location.address.trim(),
        city: formData.location.city.trim(),
        state: formData.location.state.trim(),
        country: formData.location.country.trim()
      };
      const eventData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        eventType: formData.eventType,
        cause: formData.cause,
        date: Timestamp.fromDate(eventDate),
        time: eventTime.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        }),
        location,
        link: formData.link ? formData.link.trim() : '',
        visibility: formData.visibility,
        organizer: {
          uid: currentUser.uid,
          displayName: currentUser.displayName || 'Anonymous',
          photoURL: currentUser.photoURL || ''
        }
      };

      await createEvent(
        eventData,
        currentUser.uid
      );

      setSubmitSuccess(true);
      if (onSuccess) {
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        onSuccess();
      } else {
        setTimeout(() => {
          if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          navigate('/events');
        }, 1500);
      }
    } catch (err: any) {
      console.error('Error creating event:', err);
      // Handle specific error cases
      if (err.code === 'permission-denied') {
        setSubmitError('Permission denied');
      } else if (err.message?.includes('Validation failed')) {
        setSubmitError(err.message);
      } else {
      setSubmitError('Event creation failed');
      }
    }
  };

  const handleCancel = () => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    navigate('/events');
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', my: 4 }}>
      <Typography variant="h5" gutterBottom>
        Create New Event
      </Typography>

      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Event created successfully
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              error={!!errors.title}
              helperText={errors.title}
              required
            />
          </Box>

          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              error={!!errors.description}
              helperText={errors.description}
              required
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.eventType}
                  onChange={(e) => setFormData({ ...formData, eventType: e.target.value as EventType })}
                  label="Type"
                  required
                >
                  {EVENT_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
              <FormControl fullWidth>
                <InputLabel>Cause</InputLabel>
                <Select
                  value={formData.cause}
                  onChange={(e) => setFormData({ ...formData, cause: e.target.value })}
                  label="Cause"
                  required
                >
                  {CAUSES.map((cause) => (
                    <MenuItem key={cause.id} value={cause.id}>
                      {cause.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
              <Suspense fallback={<div>Loading…</div>}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date"
                  value={formData.date}
                  onChange={(newValue: any) => setFormData({ ...formData, date: newValue })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: errors.date,
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
              </Suspense>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 50%' } }}>
              <Suspense fallback={<div>Loading…</div>}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  label="Time"
                  value={formData.time}
                  onChange={(newValue: any) => setFormData({ ...formData, time: newValue })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.time,
                      helperText: errors.time,
                      required: true,
                    },
                  }}
                />
              </LocalizationProvider>
              </Suspense>
            </Box>
          </Box>

          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Address"
              value={formData.location.address}
              onChange={(e) => setFormData({
                ...formData,
                location: { ...formData.location, address: e.target.value }
              })}
              error={!!errors.address}
              helperText={errors.address}
              required
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 33%' } }}>
              <TextField
                fullWidth
                label="City"
                value={formData.location.city}
                onChange={(e) => setFormData({
                  ...formData,
                  location: { ...formData.location, city: e.target.value }
                })}
                error={!!errors.city}
                helperText={errors.city}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 33%' } }}>
              <TextField
                fullWidth
                label="State"
                value={formData.location.state}
                onChange={(e) => setFormData({
                  ...formData,
                  location: { ...formData.location, state: e.target.value }
                })}
                error={!!errors.state}
                helperText={errors.state}
                required
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 33%' } }}>
              <TextField
                fullWidth
                label="Country"
                value={formData.location.country}
                onChange={(e) => setFormData({
                  ...formData,
                  location: { ...formData.location, country: e.target.value }
                })}
                error={!!errors.country}
                helperText={errors.country}
                required
              />
            </Box>
          </Box>

          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label="Link"
              value={formData.link || ''}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
              placeholder="Optional external link"
            />
          </Box>

          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={!!submitError}>
                {submitError ? <CircularProgress size={20} /> : 'Create Event'}
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </Paper>
  );
} 